import { useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalization } from '@ais-ui/locale-management';
import { ParsedReportParams } from '@ais-ui/websockets';

import {
  FilterType,
  MiscellaneousField,
  ReportShapeItem,
  ReportType,
  ShapeType,
} from '../../../reportsTypes';
import {
  createReportCloneFormSchema,
  ReportCloneValues,
} from '../../utils/createReportCloneFormSchema';
import { createReportTemplatePeriodFormSchema } from '../../utils/createPeriodFormSchema';
import { useDateTimeFormat } from '../../../../conversions/useDateFormat';
import { useInitializeShape } from '../initializeReportValues/useInitializeShape';
import { useInitializeFilter } from '../initializeReportValues/useInitializeFilter';
import { useInitializeMiscellaneous } from '../initializeReportValues/useInitializeMiscellaneous';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { GeoJsonDictionary } from '../../../utils';

interface UseCloneReportFormOptions {
  report: ParsedReportParams | undefined;
  reportType: ReportType;
  shapeType: ShapeType;
  filterType: FilterType;
  databaseRange: { database: { begin: Date; end: Date } } | undefined;
  isLoadingDatabaseRange: boolean;
  areResourcesLoading: boolean;
  isLoadingShareGroups: boolean;
  instantShape: boolean;
  instantShapeData: GeoJsonDictionary;
  initialIsDynamic: boolean;
  initialDynamicShape: GeoJson | undefined;
}

interface UseCloneReportFormReturn {
  form: UseFormReturn<ReportCloneValues>;
  isLoading: boolean;
}

/**
 * Hook that manages the clone report form setup, schema creation, and initial values.
 */
export function useCloneReportForm({
  report,
  reportType,
  shapeType,
  filterType,
  databaseRange,
  isLoadingDatabaseRange,
  areResourcesLoading,
  isLoadingShareGroups,
  instantShape,
  instantShapeData,
  initialIsDynamic,
  initialDynamicShape,
}: UseCloneReportFormOptions): UseCloneReportFormReturn {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const initializeShape = useInitializeShape(report, reportType);
  const initializeFilter = useInitializeFilter(report, reportType);
  const { initializeMiscellaneous } = useInitializeMiscellaneous(report, reportType);

  const shapeItems: ReportShapeItem[] | undefined = reportType.shape
    ? reportType.shape[shapeType]
    : undefined;
  const miscellaneousFields: Record<string, MiscellaneousField> | undefined =
    reportType.miscellaneous;

  // Create form schema
  const schema = useMemo(() => {
    const baseSchema = createReportCloneFormSchema(
      shapeItems,
      shapeType,
      filterType,
      miscellaneousFields,
      reportType.ships,
      false, // No template name for cloning
      instantShape,
    );

    if (!databaseRange) return baseSchema;

    const schemaWithRefinement = baseSchema.refine(
      (data: any) => data.end_datetime > data.start_datetime,
      {
        message: messages.reportsCloneTimeValidationText,
        path: ['end_datetime'],
      },
    );

    return createReportTemplatePeriodFormSchema(
      databaseRange.database.begin,
      databaseRange.database.end,
      formatDateTimeWithTimeZone(databaseRange.database.begin),
      formatDateTimeWithTimeZone(databaseRange.database.end),
      schemaWithRefinement,
    );
  }, [
    shapeItems,
    shapeType,
    filterType,
    miscellaneousFields,
    reportType.ships,
    instantShape,
    databaseRange,
    messages.reportsCloneTimeValidationText,
    formatDateTimeWithTimeZone,
  ]);

  // Compute initial values
  const initialValues = useMemo<Record<string, unknown>>(() => {
    if (areResourcesLoading || isLoadingShareGroups) {
      return {
        report_name: '',
        start_datetime: new Date(),
        end_datetime: new Date(),
        miscellaneous: { selectedGroups: [] },
        email: [],
        mailAction: '',
        countryCode: '',
        phone: '',
      };
    }

    // Get existing email(s) from report params
    const existingEmails = report?.params?.MC?.who || [];
    const existingMailAction = report?.params?.MC?.action || '';

    const startDate = report?.params?.['SDAISO'] ? new Date(report.params['SDAISO']) : new Date();
    const endDate = report?.params?.['EDAISO'] ? new Date(report.params['EDAISO']) : new Date();

    // Initialize ships data if the report type supports ships
    const shipsValues: Record<string, unknown> = {};
    if (reportType.ships) {
      // Parse ships from TL parameter
      const existingShips = report?.params?.['TL'] || [];
      shipsValues.ships = existingShips.map((ship: any) => ({
        name: ship.name ?? '',
        mmsi: ship.mmsi?.toString() ?? '',
        callSign: ship.callsign ?? '',
        imo: ship.imo?.toString() ?? '',
        type: ship.type ?? '',
      }));

      // Parse shipAttribute from SAS parameter (1 = colorBy, 0 = filterBy)
      if (reportType.ships.colorBy && reportType.ships.filterBy) {
        shipsValues.shipAttribute = report?.params?.['SAS'] === 1 ? 'colorBy' : 'filterBy';
      } else if (reportType.ships.colorBy) {
        shipsValues.shipAttribute = 'colorBy';
      } else if (reportType.ships.filterBy) {
        shipsValues.shipAttribute = 'filterBy';
      }
    }

    const baseValues = {
      report_name: report?.info?.['Job name'] ?? '',
      start_datetime: startDate,
      end_datetime: endDate,
      miscellaneous: initializeMiscellaneous(reportType.miscellaneous),
      email: existingEmails,
      mailAction: existingMailAction,
      ...initializeFilter(),
      ...shipsValues,
    };

    // Handle shape initialization based on instant shape mode
    let result;
    if (instantShape) {
      result = { ...baseValues, ...instantShapeData };
    } else if (initialIsDynamic && initialDynamicShape) {
      const shapeFieldName = reportType.shape?.shape?.[0]?.name || 'shape';
      result = { ...baseValues, [shapeFieldName]: 'Shape drawn in MDM' };
    } else {
      result = { ...baseValues, ...initializeShape() };
    }

    return result;
  }, [
    areResourcesLoading,
    isLoadingShareGroups,
    report,
    instantShape,
    instantShapeData,
    initialIsDynamic,
    initialDynamicShape,
    initializeMiscellaneous,
    initializeFilter,
    initializeShape,
    reportType.miscellaneous,
    reportType.shape,
    reportType.ships,
  ]);

  // Fix interval field format (trailing 's')
  const fixedInitialValues = useMemo(() => {
    const values = { ...initialValues };
    type MiscRecord = Record<string, unknown> & { interval?: string };
    const miscRecord = values.miscellaneous as MiscRecord | undefined;
    if (miscRecord?.interval && miscRecord.interval.endsWith('s')) {
      miscRecord.interval = miscRecord.interval.slice(0, -1);
    }
    return values;
  }, [initialValues]);

  // Create form with initial default values to ensure fields have values on first render
  const form: UseFormReturn<ReportCloneValues> = useForm<ReportCloneValues>({
    resolver: zodResolver(schema),
    defaultValues: fixedInitialValues as any,
  });

  const isLoading = areResourcesLoading || isLoadingShareGroups;

  // Reset form when initial values change (e.g., when data finishes loading)
  useEffect(() => {
    if (!isLoading) {
      form.reset(fixedInitialValues);
    }
  }, [form, fixedInitialValues, isLoading]);

  // Sync instantShapeData to form when it changes (when user draws a shape)
  useEffect(() => {
    if (!instantShape || !instantShapeData) return;

    const shapeItems = reportType.shape?.shape;
    if (shapeItems) {
      shapeItems.forEach((shapeItem) => {
        const geometry = instantShapeData[shapeItem.name];
        if (geometry) {
          form.setValue(shapeItem.name, geometry, { shouldValidate: true });
        }
      });
    }
  }, [instantShapeData, instantShape, form, reportType.shape]);

  return { form, isLoading };
}
